import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { FormlyFieldConfig, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  FmFrameworkContractLineItem,
  FmIncidentServiceLine,
  FmUnit,
  LocationClient,
} from '@core/api';
import { ToastrService } from 'ngx-toastr';
import { formatPrice } from '@shared/utils/price-formatter';
import { forkJoin, of } from 'rxjs';
import { catchError, first, map, take } from 'rxjs/operators';
import { DialogComponent } from '@shared/components/dialog/dialog.component';

export interface SubmitOfferDialogData {
  title: string;
  submitButtonLabel: string;
  locations: FmIncidentServiceLine[];
  serviceLineColumns: any[];
  fmIncidentArchObj: any[];
  isDummyFrameworkContract?: boolean;
  units?: FmUnit[];
  hideUnitPriceEdit?: boolean;
  frameworkContractLineItems?: FmFrameworkContractLineItem[];
  enableManualLineManagement?: boolean;
  showAddendumCommentField?: boolean;
  showAddendumStatusReason?: boolean;
  addendumStatusReason?: string;
  addendumComment?: string;
  equipmentByArchObjId?: Record<string, any[]>;
  equipmentColumns?: any[];
  offerDate?: Date;
  expandFirstLocationOnInit?: boolean;
}

@Component({
  selector: 'app-submit-offer-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormlyModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './submit-offer-dialog.component.html',
  styleUrls: ['./submit-offer-dialog.component.scss'],
})
export class SubmitOfferDialogComponent implements OnInit {
  form = new FormGroup({});
  formModel: any = {};
  formFields: FormlyFieldConfig[] = [];

  locations: FmIncidentServiceLine[];
  addendumComment = '';

  get filteredLocations(): FmIncidentServiceLine[] {
    return this.locations.filter(
      location =>
        location.recordNumber !== null && location.recordNumber !== undefined
    );
  }

  constructor(
    public dialogRef: MatDialogRef<SubmitOfferDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SubmitOfferDialogData,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private locationClient: LocationClient,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog
  ) {
    this.locations = JSON.parse(JSON.stringify(data.locations));
    this.addendumComment = data.addendumComment || '';
  }

  ngOnInit() {
    this.formFields = [
      {
        key: 'locationsExpandable',
        type: 'locations-expandable-editable',
        props: {
          headline:
            this.translateService.instant(
              'taskmodule.shared.label__locations'
            ) || 'Standorte',
          locations: this.locations,
          serviceLineColumns: this.data.serviceLineColumns,
          fmIncidentArchObj: this.data.fmIncidentArchObj,
          isDummyFrameworkContract: this.data.isDummyFrameworkContract || false,
          units: this.data.units || [],
          hideUnitPriceEdit: this.data.hideUnitPriceEdit || false,
          frameworkContractLineItems:
            this.data.frameworkContractLineItems || [],
          enableManualLineManagement:
            this.data.enableManualLineManagement || false,
          showAddendumStatusReason: this.data.showAddendumStatusReason || false,
          addendumStatusReason: this.data.addendumStatusReason,
          equipmentByArchObjId: this.data.equipmentByArchObjId || {},
          equipmentColumns: this.data.equipmentColumns || [],
          equipmentFilterDate: this.data.offerDate,
          expandFirstLocationOnInit:
            this.data.expandFirstLocationOnInit === true,
          disableEquipmentDocumentationUpload: true,
        },
      },
    ];

    if (this.data.showAddendumCommentField) {
      this.formFields.push({
        key: 'addendumComment',
        type: 'textarea',
        wrappers: ['form-field'],
        props: {
          label: this.translateService.instant('Kommentar Nachtragsangebot'),
          placeholder: this.translateService.instant(
            'Kommentar Nachtragsangebot'
          ),
          required: true,
          autosize: true,
          autosizeMinRows: 3,
          autosizeMaxRows: 6,
          maxLength: 2000,
          style: {
            resize: 'none',
          },
        },
      });

      this.formModel = {
        addendumComment: this.addendumComment,
      };
    }

    this.enrichMissingArchObjData();
  }

  private enrichMissingArchObjData(): void {
    const missingArchObjIds = Array.from(
      new Set(
        (this.locations || [])
          .filter(
            location =>
              !!location?.archObjId && !location?.archObj?.dtArchObjAddress
          )
          .map(location => location.archObjId as string)
      )
    );

    if (missingArchObjIds.length === 0) {
      return;
    }

    forkJoin(
      missingArchObjIds.map(archObjId =>
        this.locationClient.getArchobjById(archObjId).pipe(
          take(1),
          map(archObj => ({ archObjId, archObj })),
          catchError(() => of({ archObjId, archObj: undefined }))
        )
      )
    )
      .pipe(take(1))
      .subscribe(results => {
        const archObjMap = new Map<string, any>(
          results
            .filter(result => !!result?.archObj)
            .map(result => [result.archObjId, result.archObj])
        );

        if (archObjMap.size === 0) {
          return;
        }

        this.locations = (this.locations || []).map(location => {
          if (!location?.archObj && location?.archObjId) {
            return {
              ...location,
              archObj: archObjMap.get(location.archObjId),
            };
          }
          return location;
        });

        const existingIncidentArchObj = Array.isArray(
          this.data.fmIncidentArchObj
        )
          ? [...this.data.fmIncidentArchObj]
          : [];

        const incidentArchObjMap = new Map<string, any>(
          existingIncidentArchObj
            .filter((entry: any) => !!(entry?.archObjId || entry?.id))
            .map((entry: any) => [entry.archObjId || entry.id, entry])
        );

        archObjMap.forEach((archObj, archObjId) => {
          const existing = incidentArchObjMap.get(archObjId);
          if (existing) {
            incidentArchObjMap.set(archObjId, {
              ...existing,
              archObj: existing.archObj || archObj,
            });
            return;
          }

          incidentArchObjMap.set(archObjId, {
            archObjId,
            archObj,
          });
        });

        this.data.fmIncidentArchObj = Array.from(incidentArchObjMap.values());

        const locationsField = this.formFields.find(
          field => field.key === 'locationsExpandable'
        );
        if (locationsField?.props) {
          locationsField.props['locations'] = this.locations;
          locationsField.props['fmIncidentArchObj'] =
            this.data.fmIncidentArchObj;
        }

        this.cd.detectChanges();
      });
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onSubmit() {
    const confirmDialog = this.dialog.open(DialogComponent, {
      width: '500px',
      panelClass: 'smartportal-dialog',
      data: {
        type: 'okDismissButton',
        title: 'Angebot wird abgegeben',
        message:
          'Haben Sie alle Angebotspositionen zu den Standorten geprüft? Mit "Jetzt abgeben" wird das Angebot im System angelegt.',
        okButtonText: 'Jetzt abgeben',
        dismissButtonText: 'Abbrechen',
      },
    });

    confirmDialog
      .afterClosed()
      .pipe(first())
      .subscribe(confirmed => {
        if (!confirmed) {
          return;
        }

        if (!this.data.showAddendumCommentField) {
          this.dialogRef.close(this.filteredLocations);
          return;
        }

        this.dialogRef.close({
          locations: this.filteredLocations,
          addendumComment: this.formModel?.addendumComment || '',
        });
      });
  }

  calculateTotalSum(): string {
    const total = this.filteredLocations.reduce((sum, location) => {
      const quantity = parseFloat(String(location.quantity)) || 0;
      const unitPrice = parseFloat(String(location.unitPrice)) || 0;
      return sum + quantity * unitPrice;
    }, 0);
    return formatPrice(total);
  }
}
